/* ============================================================
   ShoLi — Renderer Module
   DOM rendering for all views. Depends on State, Toast, Confirm, ViewManager.
   ============================================================ */

import type { AddResult, AppSettings, TodoItem } from "../state";
import { ItemStatus } from "../state";

// Forward declarations — provided by app.ts composition root
type StateApi = {
  getSetting: <K extends keyof AppSettings>(key: K) => AppSettings[K];
  getListItems: () => TodoItem[];
  getAllItems: () => TodoItem[];
  sortItems: (items: TodoItem[], groupByState: boolean) => TodoItem[];
  addItem: (name: string) => AddResult;
  toggleCheck: (id: string) => boolean;
  toggleOnList: (id: string) => boolean;
  removeItem: (id: string) => boolean;
};

type ToastApi = {
  show: (message: string, duration?: number) => void;
};

type ConfirmDialogApi = {
  ask: (message: string) => Promise<boolean>;
};

export class Renderer {
  private state: StateApi;
  private toast: ToastApi;
  private confirm: ConfirmDialogApi;

  private checkingListEl: HTMLElement | null = null;
  private checkingEmptyEl: HTMLElement | null = null;
  private editListEl: HTMLElement | null = null;
  private editEmptyEl: HTMLElement | null = null;
  private searchInputEl: HTMLInputElement | null = null;
  private addItemBtnEl: HTMLButtonElement | null = null;

  constructor(state: StateApi, toast: ToastApi, confirm: ConfirmDialogApi) {
    this.state = state;
    this.toast = toast;
    this.confirm = confirm;
  }

  init(): void {
    this.checkingListEl = document.getElementById("checking-list");
    this.checkingEmptyEl = document.getElementById("checking-empty");
    this.editListEl = document.getElementById("edit-list");
    this.editEmptyEl = document.getElementById("edit-empty");
    this.searchInputEl = document.getElementById("edit-search") as HTMLInputElement;
    this.addItemBtnEl = document.getElementById("btn-add-item") as HTMLButtonElement;

    this.checkingListEl?.addEventListener("click", this.handleCheckingClick);
    this.editListEl?.addEventListener("click", this.handleEditClick);
    this.searchInputEl?.addEventListener("input", this.handleSearch);
    this.searchInputEl?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.handleAddItem();
      }
    });
    this.addItemBtnEl?.addEventListener("click", () => this.handleAddItem());
  }

  render = (): void => {
    this.renderCheckingView();
    this.renderEditView(this.searchInputEl?.value ?? "");
    this.applyTextSize();
  };

  private renderCheckingView(): void {
    const groupByState = this.state.getSetting("groupByState");
    const listItems = this.state.sortItems(this.state.getListItems(), groupByState);
    const checkingList = this.checkingListEl;
    if (!checkingList) return;
    const checkingEmpty = this.checkingEmptyEl;

    checkingList.innerHTML = "";

    if (listItems.length === 0) {
      checkingEmpty?.classList.remove("hidden");
      checkingList.classList.add("hidden");
    } else {
      checkingEmpty?.classList.add("hidden");
      checkingList.classList.remove("hidden");

      listItems.forEach((item) => {
        const el = this.createItemElement(item);
        checkingList.appendChild(el);
      });
    }
  }

  private renderEditView(filter = ""): void {
    const lowerFilter = filter.toLowerCase();
    const groupByState = this.state.getSetting("groupByState");
    const allItems = this.state.sortItems(this.state.getAllItems(), groupByState);

    const filtered = lowerFilter
      ? allItems.filter((i) => i.name.toLowerCase().includes(lowerFilter))
      : allItems;

    const editList = this.editListEl;
    if (!editList) return;
    const editEmpty = this.editEmptyEl;

    editList.innerHTML = "";

    if (filtered.length === 0) {
      editEmpty?.classList.remove("hidden");
      editList.classList.add("hidden");
    } else {
      editEmpty?.classList.add("hidden");
      editList.classList.remove("hidden");

      filtered.forEach((item) => {
        const el = this.createItemElement(item, "edit");
        editList.appendChild(el);
      });
    }

    const canAdd = filter.trim();
    const addItemBtn = this.addItemBtnEl;
    if (addItemBtn) {
      addItemBtn.disabled = !canAdd;
      addItemBtn.classList.toggle("add-btn-disabled", !canAdd);
      addItemBtn.classList.remove("hidden");
    }
  }

  private createItemElement(item: TodoItem, editView?: "edit" | null): HTMLElement {
    const el = document.createElement("div");
    const statusClass =
      item.status === ItemStatus.OffList
        ? "off"
        : item.status === ItemStatus.Unchecked
          ? "unchecked"
          : "checked";
    el.className = `list-item status-${statusClass}`;
    el.dataset.id = item.id;

    const statusEl = document.createElement("span");
    statusEl.className = "item-status";
    if (item.status === ItemStatus.Unchecked) statusEl.textContent = "\u2022";
    else if (item.status === ItemStatus.Checked) statusEl.textContent = "\u2713";

    const nameEl = document.createElement("span");
    nameEl.className = "item-name";
    nameEl.textContent = item.name;

    el.appendChild(statusEl);
    el.appendChild(nameEl);

    if (editView) {
      const trashEl = document.createElement("span");
      trashEl.className = "item-trash";
      trashEl.textContent = "\u2715";
      trashEl.title = "Remove";
      el.appendChild(trashEl);
    }

    return el;
  }

  private handleCheckingClick = (e: MouseEvent): void => {
    const el = e.target as HTMLElement;
    const item = el.closest(".list-item");
    if (!item) return;
    const id = (item as HTMLElement).dataset.id;
    if (!id) return;
    this.state.toggleCheck(id);
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(30);
    }
  };

  private handleEditClick = async (e: MouseEvent): Promise<void> => {
    const el = e.target as HTMLElement;
    const item = el.closest(".list-item");
    if (!item) return;

    if (el.classList.contains("item-trash")) {
      const nameEl = item.querySelector(".item-name");
      if (!nameEl) return;
      const name = nameEl.textContent ?? "";
      const ok = await this.confirm.ask(`Remove "${name}"?`);
      if (!ok) return;
      const id = (item as HTMLElement).dataset.id;
      if (!id) return;
      this.state.removeItem(id);
      this.toast.show(`Removed "${name}"`);
      return;
    }

    const id = (item as HTMLElement).dataset.id;
    if (!id) return;
    this.state.toggleOnList(id);
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(30);
    }
  };

  private handleSearch = (): void => {
    this.renderEditView(this.searchInputEl?.value ?? "");
  };

  handleAddItem = (): void => {
    const input = this.searchInputEl;
    if (!input) return;
    const name = input.value.trim();
    if (!name) return;

    const result = this.state.addItem(name);
    if (result.kind === "added") {
      input.value = "";
      this.toast.show(`Added "${name}"`);
    } else {
      this.state.toggleOnList(result.existingId);
      this.toast.show(`Toggled "${name}" to unchecked`);
      input.value = "";
    }
    this.render();
  };

  applyTextSize(): void {
    document.documentElement.style.fontSize = `${this.state.getSetting("textSize")}px`;
  }

  getSearchInput(): HTMLInputElement | null {
    return this.searchInputEl;
  }
}
